<template>
  <main-table-layout
      sub-menu-selector="appspanel"
  >
    <div slot="buttonsRight"/>
    <content-filter
        id="appList"
        slot="filter"
        :team-filtering="true"
        store-name="App"
        @filter="filtering"
    />
    <view-switcher
        id="mainContent"
        slot="mainContentButtons"
        :table="true"
        :thumbnails="true"
    />
    <pagination
        id="projecttablepage"
        ref="pagination"
        slot="mainContentButtons"
        :limit="paginationLimit"
        :total="$store.getters.getAppListOptions(listName, 'pagination_items')"
        @setActive="loadPaginatedData"
    />
    <div slot="mainContentButtons">
      <Button
          icon="plus"
          link="/app/add"
          permission="create" type="action-button">
        {{ $t('createNewApp') }}
      </Button>
    </div>

    <Button
        slot="mainContent"
        class="icon-button"
        icon="rotate-right"
        type="reload"
        @click="loadPaginatedData(0); $refs.pagination.setActive(0)"
    />
    <table-widget
        :key="forceReRenderKey"
        v-if="$store.getters.getViewSwitcher($route.name, 'mainContent') === 'table'"
        id="appsDefaultTable"
        slot="mainContent"
        :data="appsWithPackageTag"
        :deletable="true"
        :fields="tableFields"
        :loading="loading"
        :nav-by-clicking-row="true"
        :show-organization="true"
        :show-tags="true"
        :title-as-links="true"
        :use-team-permission="true"
        delete-permission="delete"
        edit-permission="write"
        resource="Project"
        :initial-sorting="sortObject"
        @deleteRow="deleteRow"
        @sort="sort"
        @updateContent="updateContent"
    />

    <thumbnails
        :key="forceReRenderKey"
        v-if="$store.getters.getViewSwitcher($route.name, 'mainContent') === 'thumbnails'"
        id="appsDefaultTable"
        slot="mainContent"
        :data="appsWithPackageTag"
        :deletable="true"
        :fields="thumbnailFields"
        :loading="loading"
        :nav-by-clicking-row="true"
        :title-as-links="true"
        :use-team-permission="true"
        delete-permission="delete"
        edit-permission="write"
        resource="Project"
        @sort="sort"
        @updateContent="updateContent"
        @delete-row="deleteRow"
    />
  </main-table-layout>
</template>

<script>
import Button from "../components/forms/Button";
import TableWidget from "../components/widgets/TableWidget";
import ContentFilter from "../components/widgets/filter/ContentFilter";
import MainTableLayout from "../layouts/MainTableLayout";
import ViewSwitcher from "../components/widgets/ViewSwitcher";
import Thumbnails from "../components/widgets/ThumbnailView";
import Pagination from "../components/Pagination";
import LoadOverviewMixinJs from "../components/mixins/LoadOverviewMixin.js.vue";
import {SpecialUuids} from "@/enum";

/** Maps a known package asset UUID to a human-readable label shown as a tag in the table. */
const PACKAGE_LABELS = {
  [SpecialUuids.AR_APP_PKG]:             'sfx-vhub-ar',
  [SpecialUuids.CC_LIGHT_APP_PKG]:       'sfx-vhub-cc',
  [SpecialUuids.CC_LIGHT_APP_PKG_BETA]:  'sfx-vhub-cc (beta)',
  [SpecialUuids.CC_LIGHT_APP_PKG_ALPHA]: 'sfx-vhub-cc (alpha)',
  [SpecialUuids.APP_PANEL_WEBAPP]:       'app-panel',
  [SpecialUuids.APP_PANEL_WEBAPP_BETA]:  'app-panel (beta)',
};

export default {
  name: 'Apps',
  components: {
    Button,
    TableWidget,
    ContentFilter,
    MainTableLayout,
    ViewSwitcher,
    Thumbnails,
    Pagination,
  },
  mixins: [LoadOverviewMixinJs],
  data() {
    return {
      registerMethod: 'registerAppsQueryParams',
      loadMethod: 'loadApps',
      include: 'tags,teams,instances',
      deleteMethod: 'deleteApp',
      listName: 'appList',
      forceReRenderKey: 0,
      tableFields: [
        {
          name: '',
          id: '',
          type: 'image',
        },
        {
          name: 'Name',
          id: 'name',
          type: 'string',
          sortable: true
        },
        {
          name: 'Package',
          id: 'packageTag',
          type: 'tagList',
        },
        {
          name: 'Teams',
          id: 'teams',
          type: 'teams',
        },
        {
          name: 'Type',
          id: 'type',
          type: 'string',
        },
        {
          name: 'Slug',
          id: 'slug',
          type: 'appSlug',
        },
        {
          name: 'Last Update',
          id: 'updatedAt',
          type: 'date',
          sortable: true
        },
        {
          name: 'Status',
          id: 'state',
          type: 'state',
        },
        {
          name: 'Edit',
          type: 'editButton'
        },
      ],
      thumbnailFields: [
        {
          name: '',
          id: '',
          type: 'image',
        },
        {
          name: 'Organization',
          id: 'organization',
          type: 'organization',
        },
        {
          name: 'Last Update',
          id: 'updatedAt',
          type: 'date',
          sortable: true
        },
        {
          name: 'Name',
          id: 'name',
          type: 'string',
          sortable: true
        },
        {
          name: 'Package',
          id: 'packageTag',
          type: 'tagList',
        },
        {
          name: 'Teams',
          id: 'teams',
          type: 'teams',
        },
        {
          name: 'Tags',
          id: 'tags',
          type: 'tagList',
        },
        {
          name: 'Status',
          id: 'state',
          type: 'state',
        },
        {
          name: 'Edit',
          type: 'editButton'
        },
      ],
    }
  },
  computed: {
    /**
     * Augments each app with a `packageTag` array (compatible with the `tags` column type)
     * by cross-referencing its instances against known package asset UUIDs.
     */
    appsWithPackageTag() {
      const apps = this.$store.getters.getAppsByListName(this.listName);
      const result = {};
      for (const id of Object.keys(apps)) {
        const app = apps[id];
        const instances = app.instances || [];
        const label = instances.reduce((found, inst) => {
          if (found) return found;
          return PACKAGE_LABELS[inst.assetId] || null;
        }, null);
        result[id] = {
          ...app,
          packageTag: label ? [{ name: label, id: label }] : [],
        };
      }
      return result;
    },
  },
  watch: {
    '$route.path'() {
     this.init();
    }
  },
  beforeMount() {
    this.init();
  },
  methods: {
    init() {
      this.loadData(false);
    },
    updateContent(args) {
      this.$store.dispatch('updateApp', args);
    },
  }
}
</script>
